import { useCallback, useEffect, useState } from 'react';
import type { NextRouter } from 'next/router';
import { useRouter } from 'next/router';
import { message } from 'antd';
import { Auth, Hub } from 'aws-amplify';
import type { Dispatch, SetStateAction } from 'react';

import { UsersQueryApi } from '@lib/api/endpoints';
import { isPostHogLoaded, posthog } from '@lib/posthog.lib';
import {
  clearUserProfile,
  setUserProfile,
} from '@lib/redux.lib/slices/app/appSlice';
import type { AppDispatch } from '@lib/redux.lib/store';
import { useAppDispatch } from '@lib/redux.lib/store';
import { AuthUserSession } from '@lib/types/authUser.types';
import { CASTING_ROLE_LIST } from '@lib/utils/constants';

import { ApiProxy } from '../api/apiProxy';

import { googleSignout } from './googleSignout';
import { AuthListener } from './listener';
import { refreshTokenIfNeeded } from './tokenRefresh';

const setupSession = (
  isCheckingSessionOnMount: boolean,
  setAuthUserSession: (session: AuthUserSession) => void,
  appDispatch: AppDispatch,
  router: NextRouter,
  setLoading: (loading: boolean) => void,
  logout?: (logoutMessage?: string) => void
) => {
  return Auth.currentAuthenticatedUser()
    .then(async (cognitoUser) => {
      if (isCheckingSessionOnMount) setLoading(true);

      const userId =
        cognitoUser.attributes?.['custom:castifi_user_id'] ||
        cognitoUser.signInUserSession.idToken.payload['custom:castifi_user_id'];
      const userData = await UsersQueryApi.getUser(Number.parseInt(userId, 10));

      const validCastingUser =
        CASTING_ROLE_LIST.includes(userData.casting_role) &&
        userData.are_casting_permissions_archived === false;

      if (!validCastingUser) {
        logout('You are not authorized to access this page.');

        return;
      }

      appDispatch(setUserProfile(userData));

      if (userData.casting_onboarded === false) {
        await router.push('/onboarding');
      }

      setAuthUserSession(cognitoUser);
    })
    .catch(() => {
      // Unauthenticated visits are expected; ProtectRoute handles redirects.
    })
    .finally(() => {
      if (isCheckingSessionOnMount) setLoading(false);
    });
};

const takedownSession = async (
  setAuthUserSession: Dispatch<SetStateAction<AuthUserSession>>,
  appDispatch: AppDispatch
) => {
  googleSignout();
  await (Auth as any)._storage.clear();
  await (Auth as any).cleanCachedItems();
  ApiProxy.resetToken();
  appDispatch(clearUserProfile());

  return setAuthUserSession(null);
};

type UseSessionObject = {
  authUserSession: AuthUserSession;
  loading: boolean;
  setLoading: Dispatch<SetStateAction<boolean>>;
  logout: () => void;
};

export const useSession = (): UseSessionObject => {
  const [loading, setLoading] = useState<boolean>(true);
  const [authUserSession, setAuthUserSession] = useState<AuthUserSession>(null);
  const appDispatch = useAppDispatch();
  const router = useRouter();

  const captureAuthEvent = (event: 'tokenRefresh' | 'tokenRefresh_failure') => {
    if (!isPostHogLoaded()) {
      return;
    }

    posthog.capture(event);
  };

  const logout = useCallback(
    (logoutMessage?: string) => {
      setLoading(true);
      Auth.signOut()
        .then((success) => {
          // Profile/session teardown runs via Hub `signOut` → takedownSession.
          // Clearing before signOut resolves left isAuthenticated true with no
          // profile when sign-out failed.
          if (logoutMessage) {
            message.info(logoutMessage, success);
          } else {
            message.info('successfully signed out', success);
          }

          setLoading(false);
        })
        .catch(() => {
          message.error('Error');
          setLoading(false);
        });
    },
    [setLoading]
  );

  useEffect(() => {
    const result = Hub.listen('auth', (data) =>
      AuthListener(
        data,
        () =>
          setupSession(
            false,
            setAuthUserSession,
            appDispatch,
            router,
            setLoading,
            logout
          ),
        () => takedownSession(setAuthUserSession, appDispatch),
        setLoading,
        captureAuthEvent
      )
    );

    setupSession(
      true,
      setAuthUserSession,
      appDispatch,
      router,
      setLoading,
      logout
    );

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const tokenRefreshIntervalId = setInterval(refreshTokenIfNeeded, 60 * 1000);

    return () => clearInterval(tokenRefreshIntervalId);
  }, []);

  return { authUserSession, loading, setLoading, logout };
};
